'use client';

import { IconScale } from '@vs-shop/ui/icons';
import type { ClassValue } from 'clsx';
import { NavbarUtility, NavbarUtilityCount } from '@vs-shop/commerce-ui/navbarUtilities';
import { clsxm, DEFAULT_COUNT_CEILING, readableCount } from '@vs-shop/utils';
import { useIntl } from 'react-intl';

import { Url } from '../../../../route/router';

import Link from '@vs-shop/image-link/Link';

import { productCodesInComparisonAtom } from '@vs-shop/engine/atom/productsComparison/productsComparison';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useAtomValue } from '@effect/atom-react';

type Props = {
  className?: ClassValue;
  /** The cookie-backed count the server rendered, before the atom settles. */
  initialCount: number;
};

/**
 * The comparison affordance.
 *
 * The count comes from the server on the first paint and from the atom
 * afterwards, so the badge is right before hydration and stays right after it.
 * The trailing margins are this bar's: a two- or three-digit badge overhangs
 * further to the trailing side than a one-digit one, and would otherwise touch
 * the affordance beside it.
 */
export default function NavbarProductsComparison({ className, initialCount }: Props) {
  const intl = useIntl();
  const productCodesResult = useAtomValue(productCodesInComparisonAtom);

  const productsCount = AsyncResult.match(productCodesResult, {
    onInitial: () => initialCount,
    onSuccess: (result) => result.value.length,
    onFailure: () => initialCount,
  });

  const isCountTwoDigit = productsCount > 9;
  const isCountHundredAndMore = productsCount > DEFAULT_COUNT_CEILING;
  const count = readableCount(productsCount);

  return (
    <NavbarUtility
      className={clsxm({ 'mr-0.5': isCountTwoDigit, 'mr-2': isCountHundredAndMore }, className)}
      render={
        <Link
          href={Url.productsComparison}
          title={intl.formatMessage({
            id: 'navbar.comparison.link',
            defaultMessage: 'Porovnání produktů',
          })}
        />
      }
    >
      <IconScale className="h-6 w-6 text-foreground lap:h-7 lap:w-7" />

      {productsCount > 0 ? (
        <NavbarUtilityCount
          data-slot="navbar-comparison-count"
          aria-label={intl.formatMessage(
            {
              id: 'navbar.comparison.itemsCount',
              defaultMessage: 'Produkty v porovnání: {count}',
            },
            { count }
          )}
        >
          {count}
        </NavbarUtilityCount>
      ) : null}
    </NavbarUtility>
  );
}
